import React from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';

import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';
import { mergeLiveDocI18n } from '@confluence/live-pages-utils/entry-points/mergeLiveDocI18n.ts';
import { preloadPageTitleContentProperties } from '@confluence/content-topper/entry-points/preloadPageTitleContentProperties.ts';
import { preloadDraftContentAppearance } from '@confluence/content-appearance/entry-points/preloadDraftContentAppearance.ts';
import { clearMemoizedCachedPageData } from '@confluence/live-pages-utils/entry-points/getCachedPageData.ts';
// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { mutateInterop } from '@confluence/apollo-relay-interop/entry-points/mutate.ts';
// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import type { UseWriteFragmentInteropResult } from '@confluence/apollo-relay-interop/entry-points/useWriteFragmentInterop.ts';

import type { RetrieveEditorTitleAndContent } from './useSwitchToLivePage.tsx';
import ConvertToLivePageMutation from './__generated__/ConvertToLivePageMutation.graphql.ts';
import type {
	ConvertToLivePageMutation$data,
	ConvertToLivePageMutation$variables,
} from './__generated__/ConvertToLivePageMutation.graphql.ts';
import {
	i18n as i18nErrors,
	getI18nStringForError,
	processError,
} from './convertToLivePageErrors.ts';
const i18nBase = defineMessages({
	titleConflictFlagTitle: {
		id: 'live-pages-features.converttolivepage.draft-conversion-title-conflict.title',
		defaultMessage: 'Unable to convert draft to live page',
		description:
			'error title shown to user when their page has the same title as another page, preventing them from converting it',
	},
	genericErrorFlagTitle: {
		id: 'live-pages-features.converttolivepage.generic-error.title',
		defaultMessage: 'An error occurred',
		description:
			'error title shown to user when an unknown error occurs, preventing them from converting it',
	},
});
const i18nLiveDocs = defineMessages({
	titleConflictFlagTitle: {
		id: 'live-pages-features.converttolivepage.draft-conversion-title-conflict.title.livedocs',
		defaultMessage: 'Unable to convert draft to live doc',
		description:
			'error title shown to user when their page has the same title as other content in the same space, preventing them from converting it to a live doc',
	},
});
export const i18n: {
	titleConflictFlagTitle: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	genericErrorFlagTitle: {
		id: string;
		defaultMessage: string;
		description: string;
	};
} = mergeLiveDocI18n(i18nBase, i18nLiveDocs);

const preloadProperties = async (contentId: string, spaceId: string) => {
	const tasks: Promise<any>[] = [];

	tasks.push(
		preloadPageTitleContentProperties({
			contentId,
			spaceKey: spaceId,
			fetchPolicy: 'network-only',
		}),
	);
	tasks.push(preloadDraftContentAppearance(contentId, false));

	return Promise.all(tasks);
};

export const convertDraftToLivePage = async ({
	contentId,
	spaceId = '',
	onSuccess,
	flags,
	retrieveEditorTitleAndContent,
	writeSubTypeFragment,
}: {
	contentId: string;
	spaceId?: string;
	onSuccess: () => void;
	flags: FlagsStateContainer;
	retrieveEditorTitleAndContent: RetrieveEditorTitleAndContent;
	writeSubTypeFragment: UseWriteFragmentInteropResult<any>;
}): Promise<void> => {
	try {
		const { data: mutationData } = await mutateInterop<
			ConvertToLivePageMutation$data,
			ConvertToLivePageMutation$variables
		>({
			mutation: ConvertToLivePageMutation,
			variables: { contentId },
			update: (_, { data }) => {
				if (data?.convertPageToLiveEditAction?.success) {
					writeSubTypeFragment({
						fragmentId: `Content:${contentId}`,
						data: { __typename: 'Content', subType: 'live' },
					});
					// Since graphql cache has been updated, make sure the memoization cache for getCachedPageData is cleared
					// So if getCachedPageData is called again with the same content id, it will read the updated graphql cache instead of returning the memoized data
					clearMemoizedCachedPageData();
				}
			},
		});
		const result = mutationData?.convertPageToLiveEditAction;

		if (result?.success) {
			await preloadProperties(contentId, spaceId).catch(() => {
				// Since its a preload, we don't want show an error flag
			});

			onSuccess();
			return;
		}

		result?.errors?.forEach(async (error) => {
			processError(error, contentId);

			const flagTitle =
				error.message === 'Title already exists'
					? i18n.titleConflictFlagTitle
					: i18n.genericErrorFlagTitle;
			const flagDescription = getI18nStringForError(error.message ?? '');

			const flagDescriptionValues: Record<string, string> = {};
			if (error.message === 'Title already exists') {
				const { title: pageTitle } = await retrieveEditorTitleAndContent();
				flagDescriptionValues.pageTitle = pageTitle || '';
			}

			void flags.showErrorFlag({
				title: <FormattedMessage {...flagTitle} />,
				description: <FormattedMessage {...flagDescription} values={flagDescriptionValues} />,
				isAutoDismiss: false,
			});
		});
	} catch (error) {
		processError(error, contentId);
		void flags.showErrorFlag({
			title: <FormattedMessage {...i18n.genericErrorFlagTitle} />,
			description: <FormattedMessage {...i18nErrors.unknownError} />,
			isAutoDismiss: false,
		});
	}
};

export const i18n_test: {
	titleConflictFlagTitle: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	genericErrorFlagTitle: {
		id: string;
		defaultMessage: string;
		description: string;
	};
} = i18n;
